import {
  Title,
  TextInput,
  Button,
  Container,
  Textarea,
  Stack,
  Select,
  Checkbox,
  Text,
  Group,
  Paper,
  Loader,
  ActionIcon,
  NumberInput,
  Alert,
} from "@mantine/core";
import { IconTrash, IconInfoCircle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import useToolStore, {
  headersDictToList,
  headersListToDict,
  overridesDictToList,
  overridesListToDict,
} from "./toolStore";
import { getFlows } from "../api";
import JsonSchemaImport from "./JsonSchemaImport";

export default function ToolEdit({ params }) {
  const [, setLocation] = useLocation();
  const { fetchTool, updateTool, tool } = useToolStore();
  const [flows, setFlows] = useState([]);

  useEffect(() => {
    fetchTool(params.toolId);
    getFlows().then(setFlows);
    // eslint-disable-next-line
  }, [params.toolId]);

  // Absolute = the tool names its own host. Relative = caller-hosted: the origin comes from the
  // calling app's X-Shuttle-Callback-Url header per request and is never stored here.
  const urlValidator = (value) => {
    if (!value) return null;
    if (/^https?:\/\//.test(value) || value.startsWith("/")) return null;
    return "Use a full URL (https://…) or a caller-hosted path starting with /";
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      description: "",
      parameters: { properties: [], type: "object" },
      webUrl: "",
      callbackTokenHeader: "",
      environmentOverrides: [],
      headers: [],
      toolType: "external",
      virtualToolId: "",
      agentTemplateId: "",
      maxAgentDepth: 5,
      producerFlowId: "",
      criticFlowId: "",
      maxLoopIterations: 3,
    },
    validate: {
      webUrl: urlValidator,
      environmentOverrides: (list) => {
        if (!list) return null;
        for (const item of list) {
          if (urlValidator(item.webUrl)) {
            return "Environment URLs must be a full URL (https://…) or a path starting with /";
          }
        }
        return null;
      },
    },
  });

  // Update form values when tool loads (only when we have the correct tool)
  useEffect(() => {
    if (tool && tool.id === params.toolId) {
      form.setValues({
        name: tool.name,
        description: tool.description,
        parameters: tool.parameters || { properties: [], type: "object" },
        webUrl: tool.webUrl || "",
        callbackTokenHeader: tool.callbackTokenHeader || "",
        // Legacy per-env webUrls are merged in for display and migrate into
        // environmentOverrides on the next save.
        environmentOverrides: overridesDictToList(tool.environmentOverrides, tool.webUrls),
        headers: headersDictToList(tool.headers),
        toolType: tool.toolType || "external",
        virtualToolId: tool.virtualToolId || "",
        agentTemplateId: tool.agentTemplateId || "",
        maxAgentDepth: tool.maxAgentDepth || 5,
        producerFlowId: tool.producerFlowId || "",
        criticFlowId: tool.criticFlowId || "",
        maxLoopIterations: tool.maxLoopIterations || 3,
      });
    }
    // eslint-disable-next-line
  }, [tool?.id, params.toolId]);

  const handleEditTool = (values) => {
    updateTool(params.toolId, {
      ...values,
      environmentOverrides: overridesListToDict(values.environmentOverrides),
      // Legacy webUrls were merged into environmentOverrides on load — clear them so the
      // data converges on the new structure.
      webUrls: null,
      headers: headersListToDict(values.headers),
    }).then(() => {
      setLocation("/tools");
    });
  };

  // Show loading state when tool is not loaded or when viewing a different tool
  if (!tool || tool.id !== params.toolId) {
    return (
      <Container size="sm">
        <Loader />
      </Container>
    );
  }

  const flowOptions = flows.map(f => ({ value: f.id, label: f.name }));
  const virtualToolOptions = [
    { value: "web_search", label: "Web Search" },
    { value: "image_generation", label: "Image Generation" },
    { value: "code_interpreter", label: "Code Interpreter" },
  ];

  const toolTypeOptions = [
    { value: "external", label: "External", description: "Calls your HTTP API when the LLM invokes this tool" },
    { value: "virtual", label: "Virtual", description: "Built-in LLM capability provided by the model vendor" },
    { value: "agent", label: "Agent", description: "Delegates to another Flow as a sub-agent" },
    { value: "critiqueloop", label: "Critique Loop", description: "Iteratively refines output using producer + critic flows" },
    { value: "mcp", label: "MCP", description: "Calls a tool on an external MCP server" },
  ];

  const toolTypeDescriptions = {
    external: "When the LLM calls this tool, PromptShuttle will forward the request to your HTTP endpoint and return the response.",
    virtual: "Virtual tools are built-in capabilities provided by the LLM vendor (e.g. web search, code execution). No external endpoint needed.",
    agent: "Agent tools invoke another Flow as a sub-agent, allowing multi-step LLM orchestration and recursive processing.",
    critiqueloop: "Critique Loop tools run a produce-then-critique cycle: a producer generates output, a critic evaluates it, and the loop repeats until the critic approves or max iterations are reached.",
    mcp: "MCP tools call external MCP (Model Context Protocol) servers. They are typically imported from an MCP server rather than created manually. Go to MCP Servers to add a server and import tools.",
  };

  return (
    <Container size="sm">
      <Title order={1} mb="md">
        Edit Tool &raquo;{tool.name}&laquo;
      </Title>

      <form onSubmit={form.onSubmit(handleEditTool)}>
        <Stack>
          <Group grow>
            <TextInput
              label="Name"
              placeholder="get_current_price"
              {...form.getInputProps("name")}
              radius="md"
            />
            <Select
              label="Tool Type"
              data={toolTypeOptions}
              {...form.getInputProps("toolType")}
              radius="md"
            />
          </Group>

          <Textarea
            label="Description"
            autosize
            minRows={1}
            maxRows={3}
            placeholder="Retrieves the current price of a stock"
            {...form.getInputProps("description")}
            radius="md"
          />

          {toolTypeDescriptions[form.values.toolType] && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              {toolTypeDescriptions[form.values.toolType]}
            </Alert>
          )}

          {form.values.toolType === "external" && (
            <>
              <TextInput
                label="Remote URL"
                placeholder={`https://yourapi.example.com/foo?token=abcd123`}
                description="Invoked with a GET request, arguments appended as query string. A full URL (https://…) points at a fixed host. A path starting with / makes this a caller-hosted tool: the host comes from the calling app on every request."
                {...form.getInputProps(`webUrl`)}
                radius="md"
              />

              <Alert
                icon={<IconInfoCircle size={16} />}
                color="gray"
                variant="light"
              >
                <Text size="sm">
                  <strong>Caller-hosted tools.</strong> Enter a path like{" "}
                  <code>/api/v1/tools/search?schema_id=[[schema_id]]</code> and the
                  origin is taken from the caller&apos;s{" "}
                  <code>X-Shuttle-Callback-Url</code> header — so a developer&apos;s
                  machine answers its own tool calls and production answers its own,
                  from one definition. If a request supplies no origin the call fails
                  and says so; nothing falls back to a stored URL. The origin must be
                  registered under Settings → Callback origins.
                </Text>
              </Alert>

              <TextInput
                label="Callback token header"
                placeholder="X-Tool-Token"
                description="Header carrying the caller's per-run callback token (X-Shuttle-Callback-Token) on caller-hosted calls. Leave empty for the default, X-Tool-Token."
                {...form.getInputProps(`callbackTokenHeader`)}
                radius="md"
              />

              {/* Environment overrides editor: per-env URL + headers. The LLM-facing
                  interface (name, description, parameters) stays the same across
                  environments — only the binding varies. */}
              <Group mt="md" justify="space-between" align="center">
                <Text fw={500}>Environment Overrides</Text>
                <Button
                  variant="outline"
                  onClick={() =>
                    form.insertListItem("environmentOverrides", {
                      env: "",
                      webUrl: "",
                      headers: [],
                    })
                  }
                >
                  Add Environment
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mt={-8}>
                Point this tool at a different endpoint (and credentials) per environment,
                e.g. a staging backend for the demo environment. Header overrides are merged
                over the base headers; anything not overridden inherits the base value.
              </Text>
              {form.values.environmentOverrides?.map((override, idx) => (
                <Paper withBorder p="xs" key={idx}>
                  <Stack gap="xs">
                    <Group align="flex-end">
                      <TextInput
                        label="Environment"
                        placeholder="demo"
                        {...form.getInputProps(`environmentOverrides.${idx}.env`)}
                        radius="md"
                        style={{ flex: 1 }}
                      />
                      <TextInput
                        label="URL Override"
                        placeholder="https://demo.example.com/tool"
                        {...form.getInputProps(`environmentOverrides.${idx}.webUrl`)}
                        radius="md"
                        style={{ flex: 2 }}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        mb={4}
                        onClick={() => form.removeListItem("environmentOverrides", idx)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                    <Group justify="space-between" align="center">
                      <Text size="sm" c="dimmed">
                        Header overrides for this environment
                      </Text>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() =>
                          form.insertListItem(`environmentOverrides.${idx}.headers`, {
                            key: "",
                            value: "",
                          })
                        }
                      >
                        Add Header Override
                      </Button>
                    </Group>
                    {override.headers?.map((_, hIdx) => (
                      <Group key={hIdx} align="flex-end">
                        <TextInput
                          placeholder="Authorization"
                          {...form.getInputProps(
                            `environmentOverrides.${idx}.headers.${hIdx}.key`
                          )}
                          radius="md"
                          style={{ flex: 1 }}
                        />
                        <TextInput
                          placeholder="Bearer staging-token"
                          {...form.getInputProps(
                            `environmentOverrides.${idx}.headers.${hIdx}.value`
                          )}
                          radius="md"
                          style={{ flex: 2 }}
                        />
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          mb={4}
                          onClick={() =>
                            form.removeListItem(
                              `environmentOverrides.${idx}.headers`,
                              hIdx
                            )
                          }
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              ))}

              {/* Headers editor */}
              <Group mt="md" justify="space-between" align="center">
                <Text fw={500}>HTTP Headers</Text>
                <Button
                  variant="outline"
                  onClick={() =>
                    form.insertListItem("headers", { key: "", value: "" })
                  }
                >
                  Add Header
                </Button>
              </Group>
              {form.values.headers?.map((_, idx) => (
                <Group key={idx} mb="xs" align="flex-end">
                  <TextInput
                    label="Header"
                    placeholder="Authorization"
                    {...form.getInputProps(`headers.${idx}.key`)}
                    radius="md"
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label="Value"
                    placeholder="Bearer your-token-here"
                    {...form.getInputProps(`headers.${idx}.value`)}
                    radius="md"
                    style={{ flex: 2 }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    mb={4}
                    onClick={() => form.removeListItem("headers", idx)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </>
          )}

          {form.values.toolType === "virtual" && (
            <Select
              label="Virtual Tool"
              description="Select a built-in capability provided by the LLM vendor"
              data={virtualToolOptions}
              {...form.getInputProps("virtualToolId")}
              radius="md"
            />
          )}

          {form.values.toolType === "agent" && (
            <Group grow align="flex-end">
              <Select
                label="Agent Flow"
                description="Select the flow that will be invoked as a sub-agent"
                data={flowOptions}
                searchable
                {...form.getInputProps("agentTemplateId")}
                radius="md"
              />
              <NumberInput
                label="Max Depth"
                description="Maximum recursion depth"
                min={1}
                max={10}
                {...form.getInputProps("maxAgentDepth")}
                radius="md"
              />
            </Group>
          )}

          {form.values.toolType === "critiqueloop" && (
            <Stack>
              <Group grow align="flex-end">
                <Select
                  label="Producer Flow"
                  description="The flow that generates output to be refined"
                  data={flowOptions}
                  searchable
                  {...form.getInputProps("producerFlowId")}
                  radius="md"
                />
                <Select
                  label="Critic Flow"
                  description="The flow that evaluates output. Must return JSON with {approved: bool, feedback: string}"
                  data={flowOptions}
                  searchable
                  {...form.getInputProps("criticFlowId")}
                  radius="md"
                />
              </Group>
              <NumberInput
                label="Max Iterations"
                description="Maximum number of produce-critique cycles before returning the best output"
                min={1}
                max={10}
                {...form.getInputProps("maxLoopIterations")}
                radius="md"
                style={{ maxWidth: 200 }}
              />
            </Stack>
          )}

          <Group mt="md" justify="space-between" align="center">
            <Text fw={500}>Request Parameters</Text>
            <Group>
              <JsonSchemaImport
                onImport={(params) => {
                  form.setFieldValue("parameters.properties", params);
                }}
              />
              <Button
                variant="outline"
                onClick={() =>
                  form.insertListItem("parameters.properties", {
                    name: "",
                    description: "",
                    type: "string",
                    required: false,
                  })
                }
              >
                Add Parameter
              </Button>
            </Group>
          </Group>

          {form.values.parameters?.properties.map((param, index) => (
            <Paper withBorder p="xs" mb="lg" key={index}>
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Text>{`Parameter ${index + 1}`}</Text>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      form.removeListItem("parameters.properties", index)
                    }
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
                <Group align="center">
                  <TextInput
                    style={{ flex: 1 }}
                    label="Name"
                    placeholder="The name of the parameter accepted by your API"
                    {...form.getInputProps(
                      `parameters.properties.${index}.name`
                    )}
                    radius="md"
                  />
                  <Checkbox
                    pt="lg"
                    label="Required"
                    {...form.getInputProps(
                      `parameters.properties.${index}.required`,
                      { type: "checkbox" }
                    )}
                  />
                </Group>
                <TextInput
                  label="Description"
                  placeholder="A human (and LLM) readable description of this parameter"
                  {...form.getInputProps(
                    `parameters.properties.${index}.description`
                  )}
                  radius="md"
                />
                <Group>
                  <Select
                    style={{ flex: 1 }}
                    data={["string", "number"]}
                    {...form.getInputProps(
                      `parameters.properties.${index}.type`
                    )}
                    label={`Type`}
                  />
                  <Checkbox
                    pt="lg"
                    label="Is List"
                    {...form.getInputProps(
                      `parameters.properties.${index}.isList`,
                      { type: "checkbox" }
                    )}
                  />
                </Group>
              </Stack>
            </Paper>
          ))}
          <Group justify="flex-end">
            <Button mt="lg" type="submit">
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
